import { ArrowRight, CheckCircle2, Info } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BUGUN = [
  "Toplu düzeyde bildirim (müşterinin KDV kimlik numarasına göre)",
  "Aylık veya üç aylık",
  "Detaylı fatura verisi yok",
  "KDV dolandırıcılığı riski yüksek kalmaya devam ediyor",
];

const YARIN = [
  "İşlem bazlı dijital bildirim (gerçek zamana yakın)",
  "Fatura verilerinin iletilmesi (e-fatura)",
  "AB genelinde birleşik dijital standart formatlar",
  "Daha fazla şeffaflık, daha az dolandırıcılık, daha adil rekabet",
  "AB iç pazarında e-fatura zorunlu",
];

const ASAMALAR: Array<{ title: string; detail: string; date: string }> = [
  {
    title: "Yasalaşma",
    detail: "ViDA direktifinin AB Konseyi'nde kabulü",
    date: "11 Mart 2025 (Konsey onayı)",
  },
  {
    title: "Ulusal uyumlaştırma",
    detail: "AB üye ülkeleri ViDA'yı ulusal hukuka aktarıyor",
    date: "31 Aralık 2025'e kadar (ulusal hukuka aktarım)",
  },
  {
    title: "E-fatura (B2B)",
    detail: "AB iç pazarında zorunlu e-fatura",
    date: "1 Temmuz 2030'dan itibaren",
  },
  {
    title: "Dijital bildirim (DRR)",
    detail: "Gerçek zamana yakın işlem bazlı dijital bildirim",
    date: "en geç 1 Temmuz 2030'dan itibaren, daha erken de mümkün",
  },
  {
    title: "İleri geliştirme",
    detail: "AB genelinde standartlar, genişletmeler ve optimizasyonlar",
    date: "2030'dan itibaren sürekli",
  },
];

const SIRKET_ETKILERI: Array<[string, string, string]> = [
  ["Bildirim kapsamı", "Toplu (KDV kimlik numarasına göre)", "İşlem bazlı (detaylı fatura verisi)"],
  ["Bildirim zamanı", "Aylık / üç aylık", "Gerçek zamana yakın (birkaç gün içinde)"],
  [
    "Fatura formatı",
    "Kağıt / PDF / EDI (serbest seçim)",
    "E-fatura zorunlu (yapılandırılmış formatlar)",
  ],
  [
    "Sistem gereksinimleri",
    "Daha düşük gereksinimler",
    "Veri kalitesi, BT süreçleri ve entegrasyon için daha yüksek gereksinimler",
  ],
  [
    "Uyumsuzluk riski",
    "Para cezaları, geriye dönük talepler",
    "Daha yüksek para cezaları, KDV indirimi hakkının kaybı riski",
  ],
  ["Fırsatlar", "–", "Süreç otomasyonu, daha az hata, şeffaflık, rekabet avantajı"],
];

const SAP_ETKILERI: Array<[string, string]> = [
  [
    "Veri modeli ve ana veri",
    "Fatura verilerinin genişletilmesi (örn. ek zorunlu alanlar), veri kalitesi için yüksek gereksinimler",
  ],
  [
    "Süreçler ve entegrasyon",
    "Order-to-Cash ve Procure-to-Pay süreçlerinde uyarlama, vergi dairesi bildirim sistemleriyle entegrasyon",
  ],
  [
    "SAP çözümleri",
    "SAP S/4HANA, SAP Document Compliance (eDocument Cockpit), SAP Integration Suite'in kullanımı ve genişletilmesi",
  ],
  [
    "E-fatura yetkinliği",
    "Yapılandırılmış e-fatura formatlarının desteklenmesi (örn. EN 16931, Peppol BIS)",
  ],
  [
    "Uyumluluk ve raporlama",
    "Dijital bildirimin (DRR) sağlanması, arşivleme, izlenebilirlik ve denetime hazır olma",
  ],
  [
    "Aksiyon ihtiyacı",
    "Erken proje planlaması, gap analizi, sistem uyarlamaları ve 2030'dan çok önce test & canlıya geçiş",
  ],
];

/**
 * ViDA reformunun görsel özeti. Yalnızca "vida-ab-dijital-cagda-kdv" yazısında
 * gösterilir.
 */
export function ViDAOverviewGraphicTr() {
  return (
    <section
      aria-labelledby="vida-overview-tr"
      className="rounded-2xl border border-border bg-card p-6 text-foreground md:p-8"
    >
      <h2 id="vida-overview-tr" className="text-2xl font-bold md:text-3xl">
        Bir Bakışta ViDA – Aşamalar, Tarihler ve SAP Altyapılarına Etkileri
      </h2>
      <p className="mt-3 text-muted-foreground">
        ViDA, dönemsel toplu KDV bildirimini işlem bazlı dijital bildirimle değiştiriyor ve AB iç
        pazarında e-faturayı zorunlu kılıyor.
      </p>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border p-5">
          <h3 className="text-base font-bold">Bugün (Toplu Bildirim)</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {BUGUN.map((item) => (
              <li key={item} className="flex gap-2">
                <span
                  aria-hidden="true"
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-muted-foreground"
                />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-primary/40 bg-primary/5 p-5">
          <h3 className="text-base font-bold text-primary">Yarın (ViDA)</h3>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            {YARIN.map((item) => (
              <li key={item} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <h3 className="mt-10 text-lg font-bold">Aşamalar ve Tarihler</h3>
      <ol className="mt-4 flex flex-col gap-4 md:flex-row md:flex-wrap">
        {ASAMALAR.map((stage, index) => (
          <li
            key={stage.title}
            className="flex flex-1 items-start gap-3 rounded-xl border border-border p-4 md:min-w-[15rem]"
          >
            <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
              {index + 1}
            </span>
            <span>
              <span className="block text-sm font-bold">{stage.title}</span>
              <span className="mt-1 block text-sm text-muted-foreground">{stage.detail}</span>
              <span className="mt-2 flex items-center gap-1 text-sm font-medium text-primary">
                <ArrowRight className="size-3.5" aria-hidden="true" />
                {stage.date}
              </span>
            </span>
          </li>
        ))}
      </ol>

      <h3 className="mt-10 text-lg font-bold">Şirketler İçin Sonuçlar</h3>
      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Kriter</TableHead>
              <TableHead>Bugün</TableHead>
              <TableHead>Yarın (ViDA)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SIRKET_ETKILERI.map(([kriter, bugun, yarin]) => (
              <TableRow key={kriter}>
                <TableCell className="font-medium">{kriter}</TableCell>
                <TableCell className="text-muted-foreground">{bugun}</TableCell>
                <TableCell className="text-muted-foreground">{yarin}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <h3 className="mt-10 text-lg font-bold">Mevcut SAP Altyapılarına Etkileri</h3>
      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Alan</TableHead>
              <TableHead>Açıklama</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SAP_ETKILERI.map(([alan, aciklama]) => (
              <TableRow key={alan}>
                <TableCell className="font-medium">{alan}</TableCell>
                <TableCell className="text-muted-foreground">{aciklama}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="mt-8 flex gap-3 rounded-xl border border-primary/40 bg-primary/5 p-5 text-sm text-muted-foreground">
        <Info className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
        <span>
          <strong className="text-foreground">Sonuç:</strong> Şirketler şimdiden yönlerini
          belirlemeli: e-fatura ve dijital bildirime hazır süreçler, veriler ve sistemlerle AB iç
          pazarında uyumlu bir geleceğe hazırlanmalı.
        </span>
      </p>
    </section>
  );
}
